/**
 * Cart drawer enhancements: 10-minute reservation timer (session)
 * + sync €0 Pack Complet gift lines (SD + Accessoires)
 * + sync €0 Talk Duo gift lines (Kit montage + Stickers) for checkout images.
 */
(function () {
  const STORAGE_KEY = 'eyespro_cart_reserve_until';
  const DURATION_MS = 10 * 60 * 1000;
  const GIFT_PROP = '_pack_gift';
  const LEGACY_OFFERT_KEYS = ['Offert 1', 'Offert 2', '_offert_1', '_offert_2'];

  let syncing = false;
  let syncQueued = false;

  function giftConfig() {
    const cfg = window.eyesProPackGifts || {};
    return {
      sd: String(cfg.sdVariantId || '').trim(),
      accessories: String(cfg.accessoriesVariantId || '').trim(),
      talkKit: String(cfg.talkKitVariantId || '').trim(),
      talkSticker: String(cfg.talkStickerVariantId || '').trim()
    };
  }

  function ensureDeadline() {
    const now = Date.now();
    let until = parseInt(sessionStorage.getItem(STORAGE_KEY) || '0', 10);
    if (!until || until < now) {
      until = now + DURATION_MS;
      sessionStorage.setItem(STORAGE_KEY, String(until));
    }
    return until;
  }

  function format(ms) {
    const total = Math.max(0, Math.ceil(ms / 1000));
    const m = Math.floor(total / 60);
    const s = total % 60;
    return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  }

  function tick() {
    const nodes = document.querySelectorAll('[data-ep-cart-timer-value]');
    if (!nodes.length) return;
    const remaining = ensureDeadline() - Date.now();
    const label = format(remaining);
    nodes.forEach((el) => {
      el.textContent = label;
    });
    if (remaining <= 0) {
      sessionStorage.removeItem(STORAGE_KEY);
      ensureDeadline();
    }
  }

  function itemHaystack(item) {
    return [
      item.variant_title,
      item.product_title,
      item.handle,
      item.product_handle,
      ...(item.options || []),
      ...(item.variant_options || [])
    ]
      .join(' ')
      .toLowerCase();
  }

  function isPackComplet(item) {
    if (isPackGift(item)) return false;
    return itemHaystack(item).indexOf('complet') !== -1;
  }

  function isTalkDuo(item) {
    if (isPackGift(item)) return false;
    const hay = itemHaystack(item);
    const isTalk =
      hay.indexOf('eyespro-talk') !== -1 ||
      hay.indexOf('intercom') !== -1 ||
      (item.handle || item.product_handle || '') === 'eyespro-talk';
    return isTalk && hay.indexOf('duo') !== -1;
  }

  function isPackGift(item) {
    const props = item.properties || {};
    if (props[GIFT_PROP]) return true;
    const cfg = giftConfig();
    const vid = String(item.variant_id || item.id || '');
    return (
      vid === cfg.sd ||
      vid === cfg.accessories ||
      vid === cfg.talkKit ||
      vid === cfg.talkSticker
    );
  }

  function giftKind(item) {
    const props = item.properties || {};
    const known = ['sd', 'accessoires', 'talk_kit', 'talk_sticker'];
    if (known.indexOf(props[GIFT_PROP]) !== -1) {
      return props[GIFT_PROP];
    }
    const cfg = giftConfig();
    const vid = String(item.variant_id || item.id || '');
    if (vid === cfg.sd) return 'sd';
    if (vid === cfg.accessories) return 'accessoires';
    if (vid === cfg.talkKit) return 'talk_kit';
    if (vid === cfg.talkSticker) return 'talk_sticker';
    return null;
  }

  function stripLegacyOffert(props) {
    const next = Object.assign({}, props || {});
    LEGACY_OFFERT_KEYS.forEach((key) => {
      delete next[key];
    });
    return next;
  }

  function hasLegacyOffert(item) {
    const props = item.properties || {};
    return LEGACY_OFFERT_KEYS.some((key) => props[key] != null && props[key] !== '');
  }

  async function fetchCart() {
    const res = await fetch('/cart.js', { credentials: 'same-origin' });
    if (!res.ok) throw new Error('cart.js failed');
    return res.json();
  }

  async function changeLine(item, quantity, properties) {
    const body = {
      id: String(item.key),
      quantity: quantity
    };
    if (properties) body.properties = properties;
    const res = await fetch('/cart/change.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    });
    if (!res.ok) throw new Error('cart/change failed');
    return res.json();
  }

  async function addGift(variantId, quantity, kind) {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: [
          {
            id: Number(variantId),
            quantity: quantity,
            properties: { [GIFT_PROP]: kind }
          }
        ]
      })
    });
    if (!res.ok) throw new Error('cart/add gift failed');
    return res.json();
  }

  async function syncKindForParent(kind, variantId, desiredQty, giftsByKind) {
    const lines = giftsByKind[kind] || [];
    const totalQty = lines.reduce((sum, item) => sum + (item.quantity || 0), 0);
    let changed = false;

    if (desiredQty <= 0) {
      for (const line of lines) {
        await changeLine(line, 0);
        changed = true;
      }
      return changed;
    }

    if (!lines.length) {
      await addGift(variantId, desiredQty, kind);
      return true;
    }

    const [primary, ...extras] = lines;
    for (const extra of extras) {
      await changeLine(extra, 0);
      changed = true;
    }

    const desiredProps = Object.assign({}, stripLegacyOffert(primary.properties), {
      [GIFT_PROP]: kind
    });
    const needsPropFix = primary.properties?.[GIFT_PROP] !== kind || hasLegacyOffert(primary);
    if (primary.quantity !== desiredQty || needsPropFix || totalQty !== desiredQty) {
      await changeLine(primary, desiredQty, desiredProps);
      changed = true;
    }
    return changed;
  }

  async function syncPackGifts() {
    if (syncing) {
      syncQueued = true;
      return;
    }
    const cfg = giftConfig();
    const hasCameraGifts = cfg.sd && cfg.accessories;
    const hasTalkGifts = cfg.talkKit && cfg.talkSticker;
    if (!hasCameraGifts && !hasTalkGifts) return;

    syncing = true;
    try {
      let cart = await fetchCart();
      let changed = false;

      const completItems = (cart.items || []).filter(isPackComplet);
      const completQty = completItems.reduce((sum, item) => sum + (item.quantity || 0), 0);
      const duoItems = (cart.items || []).filter(isTalkDuo);
      const duoQty = duoItems.reduce((sum, item) => sum + (item.quantity || 0), 0);

      for (const item of completItems.concat(duoItems)) {
        if (!hasLegacyOffert(item)) continue;
        await changeLine(item, item.quantity, stripLegacyOffert(item.properties));
        changed = true;
      }
      if (changed) cart = await fetchCart();

      const giftsByKind = {
        sd: (cart.items || []).filter((item) => giftKind(item) === 'sd'),
        accessoires: (cart.items || []).filter((item) => giftKind(item) === 'accessoires'),
        talk_kit: (cart.items || []).filter((item) => giftKind(item) === 'talk_kit'),
        talk_sticker: (cart.items || []).filter((item) => giftKind(item) === 'talk_sticker')
      };

      if (hasCameraGifts) {
        if (await syncKindForParent('sd', cfg.sd, completQty, giftsByKind)) changed = true;
        if (await syncKindForParent('accessoires', cfg.accessories, completQty, giftsByKind)) {
          changed = true;
        }
      }

      if (hasTalkGifts) {
        // Each Duo pack line unit includes 2 kits + 5 stickers.
        const duoPacks = duoQty > 0 ? duoQty : 0;
        const kitQty = duoPacks * 2;
        const stickerQty = duoPacks * 5;
        if (await syncKindForParent('talk_kit', cfg.talkKit, kitQty, giftsByKind)) changed = true;
        if (await syncKindForParent('talk_sticker', cfg.talkSticker, stickerQty, giftsByKind)) {
          changed = true;
        }
      }

      if (changed) {
        document.dispatchEvent(new CustomEvent('cart:refresh'));
        if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS?.cartUpdate) {
          window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'eyespro-pack-gifts' });
        }
      }
    } catch (e) {
      /* non-blocking */
    } finally {
      syncing = false;
      if (syncQueued) {
        syncQueued = false;
        syncPackGifts();
      }
    }
  }

  function scheduleSync() {
    setTimeout(syncPackGifts, 150);
  }

  tick();
  setInterval(tick, 1000);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleSync);
  } else {
    scheduleSync();
  }

  document.addEventListener('cart:updated', scheduleSync);
  document.addEventListener('cart:refresh', scheduleSync);

  const originalFetch = window.fetch;
  if (typeof originalFetch === 'function') {
    window.fetch = function () {
      const args = arguments;
      return originalFetch.apply(this, args).then((response) => {
        try {
          const req = args[0];
          const url = typeof req === 'string' ? req : req && req.url;
          if (url && /\/cart\/(add|change|update|clear)/.test(url)) {
            scheduleSync();
          }
        } catch (e) {
          /* ignore */
        }
        return response;
      });
    };
  }
})();
